import * as React from "react"

import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

// 非学生（教师等）端：还原 github/dev 旧版主题切换器（sage / ocean / taro 三色点）。
// 与学生端 theme-selector.tsx（orange 四色轮盘，写 html[data-theme]）完全独立：
// 这里写 document.body 的 data-theme（与 data-skin=legacy 同元素，配合 global.css
// 的 [data-skin=legacy][data-theme=…] 变体），localStorage 用独立 key，互不干扰。
const STORAGE_KEY = "eeelt_theme_legacy"
const DEFAULT_THEME = "sage"

const THEMES = [
  { value: "sage", label: "Sage", swatch: "#8A9A5B" },
  { value: "ocean", label: "Ocean", swatch: "#3B7C9E" },
  { value: "taro", label: "Taro", swatch: "#AF9AC9" },
] as const

type ThemeValue = typeof THEMES[number]["value"]

type ThemeSelectorLegacyProps = {
  className?: string
  compact?: boolean
  showLabel?: boolean
}

function getInitialTheme(): ThemeValue {
  if (typeof window === "undefined") {
    return DEFAULT_THEME
  }
  const stored = window.localStorage.getItem(STORAGE_KEY)
  const match = THEMES.find((theme) => theme.value === stored)
  return match?.value ?? DEFAULT_THEME
}

export default function ThemeSelectorLegacy({
  className,
  compact = false,
  showLabel = true,
}: ThemeSelectorLegacyProps) {
  const { t } = useTranslation()
  const [theme, setTheme] = React.useState<ThemeValue>(getInitialTheme)

  React.useEffect(() => {
    document.body.setAttribute("data-theme", theme)
    window.localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  return (
    <div
      className={cn(
        compact
          ? "inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-2 py-1"
          : "flex flex-col items-center gap-2",
        className
      )}
    >
      {showLabel && (
        <span className={cn("text-xs text-gray-400", compact && "text-[11px]")}>
          {t("common.themeLabel", { defaultValue: '主題色' })}
        </span>
      )}
      <div className={cn("flex items-center gap-2", compact && "gap-1.5")}>
        {THEMES.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-label={option.label}
            title={option.label}
            aria-pressed={theme === option.value}
            onClick={() => setTheme(option.value)}
            className={cn(
              "rounded-full border border-white/70 shadow-sm transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              compact ? "h-4 w-4" : "h-5 w-5",
              theme === option.value
                ? "scale-110 ring-2 ring-primary"
                : "opacity-70 hover:opacity-100"
            )}
            style={{ backgroundColor: option.swatch }}
          />
        ))}
      </div>
    </div>
  )
}
